import React from 'react';
import Module from 'Components/Core/Module';
import { registerBrokerageDataListener, notifyBrokerageDataChanged } from 'Components/Banking/brokerageDataBus';
import './Brokerage.css';

// IB-Orderbuch (Chat vom 2026-08-07, Phase 2 der IBKR-Integration) - Pendant
// zu BrokerageOrderbook.js (Comdirect), aber ohne Verbindungsauswahl/TAN -
// siehe IbBrokerageOverview.js-Kommentar. Zeigt die komplette lokale
// Order-Historie (ibBrokerageOrders), Stornieren nur bei offenem Status.
//
// Chat vom 2026-08-07 (Nutzerfeedback): Filter-Tabs und sprechende
// Text-Status (statt Farbpunkt) analog BrokerageOrderbook.js (Comdirect) -
// IBs eigene Status-Strings (siehe TWS-API-Doku "Order.orderStatus") sind
// technischer Natur, werden hier auf verstaendliche deutsche Begriffe
// abgebildet.
const IB_STATUS_LABELS = {
	PendingSubmit: 'Wird übermittelt',
	PendingCancel: 'Storno wird verarbeitet',
	PreSubmitted: 'Vorgemerkt',
	Submitted: 'Übermittelt',
	ApiPending: 'Wird verarbeitet',
	ApiCancelled: 'Storniert',
	Cancelled: 'Storniert',
	Filled: 'Ausgeführt',
	Inactive: 'Inaktiv',
	Pending: 'Ausstehend',
};

const OPEN_STATUSES = ['PendingSubmit', 'PreSubmitted', 'Submitted', 'ApiPending', 'Pending', 'PendingCancel'];
const EXECUTED_STATUSES = ['Filled'];
const CANCELLED_STATUSES = ['Cancelled', 'ApiCancelled', 'Inactive'];

const STATUS_FILTERS = [
	{ key: 'ALL', label: 'Alle' },
	{ key: 'OPEN', label: 'Offen' },
	{ key: 'EXECUTED', label: 'Ausgeführt' },
	{ key: 'CANCELLED', label: 'Gestrichen' },
];

function matchesStatusFilter(order, filter) {
	if (filter === 'ALL') return true;
	if (filter === 'OPEN') return OPEN_STATUSES.includes(order.orderstatus);
	if (filter === 'EXECUTED') return EXECUTED_STATUSES.includes(order.orderstatus);
	if (filter === 'CANCELLED') return CANCELLED_STATUSES.includes(order.orderstatus);
	return true;
}

function statusVariant(orderstatus) {
	if (OPEN_STATUSES.includes(orderstatus)) return 'open';
	if (EXECUTED_STATUSES.includes(orderstatus)) return 'executed';
	if (CANCELLED_STATUSES.includes(orderstatus)) return 'cancelled';
	return 'unknown';
}

export default function IbBrokerageOrderbook(props) {
	const x = props.x;
	const [orders, setOrders] = React.useState(null);
	const [error, setError] = React.useState('');
	const [cancelling, setCancelling] = React.useState(null);
	// Default "Offen" (analog BrokerageOrderbook.js/Comdirect) - die meist
	// relevanten Orders beim Oeffnen direkt sichtbar.
	const [statusFilter, setStatusFilter] = React.useState('OPEN');
	// Chat vom 2026-08-08 (Bugreport in IbBrokerageOverview.js: "Klick auf
	// Reload-Icon bleibt wirkungslos") - gleicher fehlender visueller
	// Feedback-Zustand wie dort, hier direkt mitgefixt.
	const [reloading, setReloading] = React.useState(false);
	// Chat vom 2026-08-09 (Bugreport: Orderbuch nach Stornieren nicht
	// aktualisiert) - IBs Stornierung ist asynchron: cancelOrder liefert
	// zunaechst nur "PendingCancel"/den Vorher-Status zurueck, der finale
	// "Cancelled"/"ApiCancelled"-orderStatus trifft erst 1-2s spaeter ein und
	// wird dann serverseitig gespiegelt. Ein Sofort-Reload zeigt daher noch
	// den offenen Stand - deshalb zusaetzliche verzoegerte Nachladungen.
	// Timer-Handles zum sauberen Abbruch beim Unmount.
	const recheckTimers = React.useRef([]);

	function scheduleRechecks() {
		recheckTimers.current.forEach(clearTimeout);
		recheckTimers.current = [2000, 5000].map((delay) => setTimeout(loadOrders, delay));
	}

	function loadOrders(onDone) {
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageOrderList', {}, (res) => {
			if (res.data.returncode !== 1) {
				setError('Orderbuch konnte nicht geladen werden.');
				if (onDone) onDone();
				return;
			}
			setOrders(res.data.content.orders || []);
			if (onDone) onDone();
		});
	}

	function reload() {
		setReloading(true);
		loadOrders(() => setReloading(false));
	}

	React.useEffect(() => {
		loadOrders();
		const unregister = registerBrokerageDataListener(x, loadOrders);
		return () => {
			unregister();
			recheckTimers.current.forEach(clearTimeout);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Chat vom 2026-08-07 (Nutzerfeedback: "nie diese hässlichen
	// System-Dialoge") - x.wau.yesNo() ist der app-eigene, gestylte
	// Bestätigungsdialog (siehe WebappUtility.js, bereits genutzt in
	// MarketdataGroupSelector.js) statt des browsereigenen window.confirm().
	function cancelOrder(orderId) {
		x.wau.yesNo('Order ' + orderId + ' wirklich stornieren?', (result) => {
			if (result !== 1) {
				return;
			}
			setCancelling(orderId);
			x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageCancelOrder', { orderId }, (res) => {
				setCancelling(null);
				if (res.data.returncode !== 1 || res.data.content?.error) {
					setError(res.data.content?.error || 'Stornieren fehlgeschlagen.');
					return;
				}
				notifyBrokerageDataChanged(x);
				loadOrders();
				// Finaler "Cancelled"-Status kommt async 1-2s spaeter (siehe
				// recheckTimers oben) - nachfassen, sonst bleibt die Order in
				// der Anzeige noch offen.
				scheduleRechecks();
			});
		});
	}

	const filteredOrders = (orders || []).filter((order) => matchesStatusFilter(order, statusFilter));

	return (
		<Module id={props.id} x={x} classes="module-ib-brokerage-orderbook" title={props.title} display="d-block">
			<div className="brokerage-toolbar">
				<i className={'bi bi-arrow-repeat brokerage-action-icon brokerage-toolbar-reload' + (reloading ? ' brokerage-icon-spin' : '')} title="Neu laden" onClick={reload}></i>
			</div>
			{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
			<div className="brokerage-status-filter">
				{STATUS_FILTERS.map((filter) => (
					<span
						key={filter.key}
						className={'brokerage-status-filter-item' + (statusFilter === filter.key ? ' brokerage-status-filter-active' : '')}
						onClick={() => setStatusFilter(filter.key)}
					>
						{filter.label}
					</span>
				))}
			</div>
			{orders === null && <p>Lade…</p>}
			{orders !== null && orders.length === 0 && <p className="brokerage-hint">Noch keine Orders.</p>}
			{orders !== null && orders.length > 0 && filteredOrders.length === 0 &&
				<p className="brokerage-hint">Keine Orders in dieser Kategorie.</p>
			}
			{filteredOrders.length > 0 &&
				<div className="brokerage-table-scroll">
					<div className="brokerage-order-row brokerage-order-row-header">
						<span>Symbol</span>
						<span>Richtung</span>
						<span>Typ</span>
						<span>Stück</span>
						<span>Gefüllt</span>
						<span>Limit</span>
						<span>Status</span>
						<span></span>
					</div>
					{filteredOrders.map((order) => {
						const cancellable = OPEN_STATUSES.includes(order.orderstatus);
						return (
							<div className="brokerage-order-row" key={order.id}>
								<span className="brokerage-order-instrument">{order.symbol}</span>
								<span className={order.action === 'SELL' ? 'brokerage-side-sell' : 'brokerage-side-buy'}>{order.action === 'SELL' ? 'Verkauf' : 'Kauf'}</span>
								<span>{order.ordertype}</span>
								<span>{order.totalquantity}</span>
								<span>{order.filledquantity ?? '0'}</span>
								<span>{order.limitprice ?? '–'}</span>
								{/* Sprechender Text statt Farbpunkt (Nutzerfeedback, Chat vom
								    2026-08-07: "das mit den verschiedenfarbigen Punkten ist mir
								    nicht klar genug") - direkt als Pille in der Zeile, IB hat
								    (anders als Comdirect) keine eigene aufklappbare
								    Detailansicht, daher hier nicht nur ein Punkt. */}
								<span>
									<span className={'brokerage-status-pill brokerage-status-pill-' + statusVariant(order.orderstatus)}>
										{IB_STATUS_LABELS[order.orderstatus] || order.orderstatus}
									</span>
								</span>
								<span className="brokerage-order-actions">
									{cancellable &&
										<i
											className={'bi bi-x-circle brokerage-action-icon brokerage-action-sell' + (cancelling === order.orderid ? ' disabled' : '')}
											title="Order stornieren"
											onClick={() => cancelling !== order.orderid && cancelOrder(order.orderid)}
										></i>
									}
								</span>
							</div>
						);
					})}
				</div>
			}
		</Module>
	);
}
